/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import { createRef } from 'react';
import { LeftNavigation } from '../index.js';
import type { DSLeftNavigationT } from '../index.js';
import { ITEM_TYPES } from '../constants/index.js';
// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = DSLeftNavigationT.Props;
type ComponentPropsInternals = DSLeftNavigationT.InternalProps;
type ComponentPropsDefaultProps = DSLeftNavigationT.DefaultProps;
type ComponentPropsOptionalProps = DSLeftNavigationT.OptionalProps;

const ref = createRef<{ setFocusedItem: React.Dispatch<React.SetStateAction<string | null>> }>();

const testOptionalProps: ComponentPropsOptionalProps = {
  selectedItem: 'selected item',
  selectedParent: 'parent',
  disableDefaultSelection: true,
  withoutBodyShadow: true,
  actionRef: ref,
  HeaderComponent: {
    type: ITEM_TYPES.LEFT_NAV_ITEM_HEADER,
    dsId: 'application-header-id',
    CollapsedComponent: () => <div />,
    itemOpts: {
      label: 'Notifications',
    },
  },
  BodyHeaderComponent: {
    type: ITEM_TYPES.LEFT_NAV_ITEM_SUBMENU,
    dsId: 'application-submenu-forms-id',
    CollapsedComponent: () => <div />,
    itemOpts: {
      label: 'Forms',
    },
  },
};

// difference Props and InternalProps is that InternalProps has all the default props filled in
//  Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {};
const testProps: ComponentPropsForApp = {
  ...testOptionalProps,
  ...testPartialDefaults,
};
const testPropsAsSyntax = {
  ...testOptionalProps,
  ...testPartialDefaults,
} as ComponentPropsForApp;
// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  expanded: true,
  expandedWidth: '100px',
  openedItem: 'opened item',
  items: [
    {
      type: ITEM_TYPES.LEFT_NAV_ITEM_HEADER,
      dsId: 'application-header-id',
      CollapsedComponent: () => <div />,
      itemOpts: {
        label: 'Notifications',
      },
    },
  ],
  footerLabel: '',
  onSelectedChange: () => null,
  onFocusChange: () => null,
  onFooterExpand: () => null,
  onFooterClose: () => null,
  labelOverflow: 'wrap',
  onItemClick: () => null,
  isSkeleton: false,
  loading: false,
  hideFooterPipe: false,
};
const testInternalProps: ComponentPropsInternals = {
  ...testOptionalProps,
  ...testCompleteDefaults,
};
const testInternalPropsAsSyntax = {
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  expanded: true,
  expandedWidth: '100px',
  openedItem: 'opened item',
  items: [
    {
      type: ITEM_TYPES.LEFT_NAV_ITEM_HEADER,
      dsId: 'application-header-id',
      CollapsedComponent: () => <div />,
      itemOpts: {
        label: 'Notifications',
      },
    },
  ],
  footerLabel: '',
  onSelectedChange: () => null,
  onFocusChange: () => null,
  onFooterExpand: () => null,
  onFooterClose: () => null,
  labelOverflow: 'wrap',
  onItemClick: () => null,
  isSkeleton: false,
  loading: false,
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  expanded: true,
  expandedWidth: '100px',
  openedItem: 'opened item',
  items: [
    {
      type: ITEM_TYPES.LEFT_NAV_ITEM_HEADER,
      dsId: 'application-header-id',
      CollapsedComponent: () => <div />,
      itemOpts: {
        label: 'Notifications',
      },
    },
  ],
  footerLabel: '',
  onSelectedChange: () => null,
  onFocusChange: () => null,
  onFooterExpand: () => null,
  onFooterClose: () => null,
  labelOverflow: 'wrap',
  onItemClick: () => null,
  isSkeleton: false,
  loading: false,
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  expanded: true,
  expandedWidth: '100px',
  openedItem: 'opened item',
  footerLabel: '',
  onSelectedChange: () => null,
  onFocusChange: () => null,
  onFooterExpand: () => null,
  onFooterClose: () => null,
  labelOverflow: 'wrap',
  onItemClick: () => null,
  isSkeleton: false,
  loading: false,
} as const;

const ComponentDummy = () => <div />;

const ExampleUsageComponent = () => (
  <>
    {/* works with explicitly casted props, all syntaxes */}
    <LeftNavigation {...testExplicitDefinition} />
    <LeftNavigation {...testInferedTypeCompatibility} />
    <LeftNavigation {...testDefinitionAsConst} />
    {/* works with inline values */}
    <LeftNavigation
      expanded
      expandedWidth="100px"
      openedItem="opened item"
      items={[
        {
          type: ITEM_TYPES.LEFT_NAV_ITEM_HEADER,
          dsId: 'application-header-id',
          CollapsedComponent: ComponentDummy,
          itemOpts: {
            label: 'Notifications',
          },
        },
      ]}
      footerLabel=""
      onSelectedChange={() => null}
      onFocusChange={() => null}
      onFooterExpand={() => null}
      onFooterClose={() => null}
      labelOverflow="wrap"
      onItemClick={() => null}
    />
  </>
);
